import { Alert, Box, Button, Card, CircularProgress, Grid, Snackbar, Stack, TextField, Typography, useTheme, alpha, Container, InputAdornment } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { clearUserState, resetPassword } from '../../state/slices/user'
import validatePassword from '../../utils/passwordValidator'
import { Lock, LockReset, ArrowBack } from '@mui/icons-material'

function ResetPassword() {
  let theme = useTheme()
  let userState = useSelector(state => state.user)
  let { token } = useParams()
  let navigate = useNavigate()
  let dispatch = useDispatch()
  const { t } = useTranslation()

  let [submitStatus, setSubmitStatus] = useState('')
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});

  if (userState.successMsg) {
    dispatch(clearUserState())
    navigate('/login')
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Perform validation on the individual field as the user types
    validateField(e.target.name, e.target.value);
  };

  const validateField = (fieldName, value) => {
    const validationErrors = { ...errors };

    switch (fieldName) {
      case 'password':
        validationErrors.password = value.trim() ? validatePassword(String(value)) : t('validation.passwordRequired');
        break;
      case 'confirmPassword':
        validationErrors.confirmPassword = value.trim() ? value === formData.password ? '' : t('validation.passwordsDoNotMatch') : t('resetPassword.confirmationRequired');
        break;
      default:
        break;
    }

    setErrors((prev) => { return { ...prev, ...validationErrors } });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = {};

    Object.keys(formData).forEach((fieldName) => {
      validateField(fieldName, formData[fieldName]);
      if (errors[fieldName]) {
        validationErrors[fieldName] = errors[fieldName];
      }
    });

    setSubmitStatus(prev => prev + 'x')

  };

  useEffect(() => {
    if (submitStatus && Object.keys(errors).filter(key => errors[key]).length === 0) {
      dispatch(resetPassword({ formData, token }));
      console.log('Form submitted:', formData);
      setSnackbarOpen(true)

    } else {
      dispatch(clearUserState())
    }
  }, [submitStatus])

  const snackbarHandleClose = () => {
    setSnackbarOpen(false)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 4,
      }}
    >
      {!userState.loading &&
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={snackbarHandleClose}
          message={userState.successMsg || userState.errorMsg}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      }

      <Container maxWidth="lg">
        <Grid container spacing={0} alignItems="stretch">
          {/* Left Image Section */}
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                minHeight: '600px',
                borderRadius: '4px 0 0 4px',
                overflow: 'hidden',
                boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <img 
                src="/6321602.jpg" 
                alt="Reset Password" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover' 
                }} 
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                }}
              >
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                  <LockReset sx={{ fontSize: 80, mb: 2, opacity: 0.95 }} />
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                    {t('resetPassword.imageTitle')}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.95, lineHeight: 1.6 }}>
                    {t('resetPassword.imageSubtitle')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Form */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '0 4px 4px 0',
                p: { xs: 4, md: 5 },
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {/* Logo/Header Section */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2.5,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                    mb: 2,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <LockReset sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  {t('resetPassword.title')}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                  }}
                >
                  {t('resetPassword.subtitle')}
                </Typography>
              </Box>

              <Stack spacing={3}>
                <TextField
                  label={t('resetPassword.password')}
                  variant='outlined'
                  name='password'
                  type='password'
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        }
                      },
                      '&.Mui-focused': {
                        bgcolor: 'white',
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                      }
                    }
                  }}
                />
                <TextField
                  label={t('resetPassword.confirmPassword')}
                  variant='outlined'
                  name='confirmPassword'
                  type='password'
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword ? errors.confirmPassword : formData.confirmPassword && formData.confirmPassword === formData.password ? t('validation.passwordMatched') : ''}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        }
                      },
                      '&.Mui-focused': {
                        bgcolor: 'white',
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      color: formData.confirmPassword && formData.confirmPassword === formData.password && !errors.confirmPassword ? 'success.main' : 'inherit',
                    }
                  }}
                />

                {userState.errorMsg && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      borderRadius: 3,
                      bgcolor: alpha('#d32f2f', 0.1),
                    }}
                  >
                    {userState.errorMsg}
                  </Alert>
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={userState.loading}
                  fullWidth
                  size="large"
                  sx={{
                    background: theme.palette.primary.main,
                    color: theme.palette.secondary.main,
                    textTransform: 'capitalize',
                    py: 1.75,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                      background: theme.palette.primary.dark,
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.primary.main, 0.5),
                      transform: 'none',
                    }
                  }}
                  disableElevation
                  variant='contained'
                >
                  {userState.loading ? (
                    <CircularProgress size={25} color="inherit" />
                  ) : (
                    <>
                      <LockReset sx={{ mr: 1.5, fontSize: 22 }} />
                      {t('resetPassword.submit')}
                    </>
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Link
                    to="/login"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      textDecoration: 'none',
                      gap: '4px',
                    }}
                  >
                    <ArrowBack sx={{ fontSize: 18 }} />
                    {t('resetPassword.backToLogin')}
                  </Link>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default ResetPassword
